import { useEffect, useState } from "react";
import { ShieldCheck, Palette, KeyRound, Smartphone, UserSquare2, Copy, Download, RefreshCw, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { tierLabel, isPaidTier } from "@/lib/tiers";
import { useAuth } from "@/context/AuthContext";
import IntegrationsPanel from "@/components/IntegrationsPanel";
import WebhookIngestPanel from "@/components/WebhookIngestPanel";
import DealPreferencesPanel from "@/components/DealPreferencesPanel";
import EnrichmentSourcesPanel from "@/components/EnrichmentSourcesPanel";
import { VerifyExistingPanel } from "@/pages/submit/SellerAuthGate";
import {
  getActiveTimezone, detectTimezone, isAutoTimezone, setActiveTimezone,
  listTimezones, fmtDateTime,
} from "@/lib/datetime";

export default function Settings() {
  const { user } = useAuth();
  return (
    <div className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Account</div>
        <h1 className="font-heading text-4xl tracking-tight">Settings</h1>
      </header>

      {/* BLOCK 171 Item 4 ,  a reachable "verify your email" step from inside
          the account page itself, not just buried behind the submission
          flow. A legacy specialist/investor/seller whose own pending
          listing lost visibility (email_verified_at required for that
          exception) needs a way to recover it without starting a new
          submission. */}
      {user && !["admin", "super_admin", "manager"].includes(user.role) && !user.email_verified_at && (
        <div data-testid="settings-verify-email-section">
          <VerifyExistingPanel />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <ProfileSection user={user} />
        <MFASection user={user} />
      </div>

      <ChangePasswordSection />

      <TimezoneSection />

      <SenderProfileSection />

      <IntegrationsPanel />

      <WebhookIngestPanel />
      {(user?.role === "admin" || user?.role === "super_admin") && <EnrichmentSourcesPanel />}
      {(user?.role === "admin" || user?.role === "super_admin") && <FLDigestTogglePanel />}

      <DealPreferencesPanel />

      {(user?.role === "admin" || user?.role === "super_admin") && <BrandSection user={user} />}
    </div>
  );
}

function ChangePasswordSection() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated.");
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ae-card p-6" data-testid="change-password-section">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="h-4 w-4 text-emerald-400" />
        <h2 className="font-heading text-xl tracking-tight">Change password</h2>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)}
               placeholder="Current password" autoComplete="current-password"
               data-testid="cp-current"
               className="bg-white/3 border border-white/10 rounded-sm px-3 py-2 text-sm text-slate-200" />
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)}
               placeholder="New password (min 6)" autoComplete="new-password"
               data-testid="cp-new"
               className="bg-white/3 border border-white/10 rounded-sm px-3 py-2 text-sm text-slate-200" />
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
               placeholder="Confirm new password" autoComplete="new-password"
               data-testid="cp-confirm"
               className="bg-white/3 border border-white/10 rounded-sm px-3 py-2 text-sm text-slate-200" />
      </div>
      <button onClick={submit} disabled={busy || !cur || !next || !confirm}
              data-testid="cp-submit"
              className="mt-4 px-4 py-2 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 disabled:opacity-40">
        {busy ? "Updating…" : "Update password"}
      </button>
    </div>
  );
}

function TimezoneSection() {
  const [auto, setAuto] = useState(isAutoTimezone());
  const [tz, setTz] = useState(getActiveTimezone());
  const detected = detectTimezone();
  const zones = listTimezones();
  const now = new Date().toISOString();

  const apply = (nextAuto, nextTz) => {
    setActiveTimezone(nextAuto ? "auto" : nextTz);
    setAuto(nextAuto);
    setTz(getActiveTimezone());
    toast.success(
      nextAuto ? `Using your detected timezone (${detected})` : `Timezone set to ${nextTz}`
    );
  };

  return (
    <div className="ae-card p-6" data-testid="timezone-section">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="h-4 w-4 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Timezone</div>
      </div>
      <p className="text-sm text-slate-400 mb-4 max-w-2xl">
        All timestamps are stored in UTC and shown in your local time. We detected{" "}
        <span className="text-slate-200 font-mono">{detected}</span> from your browser. Override it
        below if you want times shown in a different zone.
      </p>

      <label className="inline-flex items-center gap-2 cursor-pointer mb-3">
        <input
          type="checkbox"
          checked={auto}
          onChange={(e) => apply(e.target.checked, tz)}
          className="accent-emerald-500"
          data-testid="timezone-auto-toggle"
        />
        <span className="text-sm">Automatic (use my browser&apos;s timezone)</span>
      </label>

      {!auto && (
        <select
          value={tz}
          onChange={(e) => apply(false, e.target.value)}
          data-testid="timezone-select"
          className="block w-full max-w-sm bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-sm"
        >
          {zones.map((z) => (
            <option key={z} value={z}>{z}</option>
          ))}
        </select>
      )}

      <div className="mt-4 text-xs text-slate-500">
        Current time in your active zone:{" "}
        <span className="text-emerald-300 font-mono" data-testid="timezone-preview">{fmtDateTime(now)}</span>
      </div>
    </div>
  );
}


function FLDigestTogglePanel() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/digest/settings").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!state) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/digest/settings", { fl_digest_enabled: !state.fl_digest_enabled });
      toast.success(r.data.fl_digest_enabled ? "Digest resumed" : "Digest paused");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const enabled = state.fl_digest_enabled;
  return (
    <div className="ae-card p-6" data-testid="fl-digest-toggle-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Admin · Notifications</div>
          <h2 className="font-heading text-xl tracking-tight">Florida Wind-Down Digest</h2>
          <p className="text-sm text-slate-400 mt-2">Pause Digest Emails. When paused, the weekly APScheduler job logs a skip record; the admin test-send endpoint still works.</p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Status</div>
              <div className={`font-mono mt-1 ${enabled ? "text-emerald-400" : "text-amber-400"}`} data-testid="fl-digest-status">
                {enabled ? "ACTIVE" : "PAUSED"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Last Send</div>
              <div className="text-slate-300 mt-1 font-mono">{state.last_send_at ? fmtDateTime(state.last_send_at) : "Never"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Recipients (last run)</div>
              <div className="text-slate-300 mt-1 font-mono tabular-nums">{state.last_recipient_count ?? 0}</div>
            </div>
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={busy}
          data-testid="fl-digest-toggle-btn"
          className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-slate-700"}`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
    </div>
  );
}

function ProfileSection({ user }) {
  return (
    <div className="ae-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="h-4 w-4 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Profile</div>
      </div>
      <div className="space-y-2 text-sm">
        <Row k="Name" v={user?.name} />
        <Row k="Email" v={user?.email} />
        <Row k="Role" v={user?.role?.replace("_", " ")} />
        <Row k="Tier" v={<span className="font-mono uppercase text-emerald-400">{tierLabel(user?.tier)}</span>} />
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-white/5 py-1.5">
      <span className="text-slate-500 capitalize">{k}</span>
      <span className="text-slate-200">{v || "N/A"}</span>
    </div>
  );
}

function MFASection({ user }) {
  // BLOCK 36: extended state machine
  //   idle    → user has no MFA
  //   setup   → /setup called, QR + code input rendered
  //   codes   → /verify succeeded OR regenerate succeeded ,  showing the 10
  //             one-time codes the backend just minted. This step MUST NOT
  //             be skipped or auto-dismissed; earlier versions of this
  //             component silently discarded them.
  //   regen   → user is on the "enabled" screen and clicked Regenerate;
  //             prompting for a fresh TOTP to satisfy the step-up gate.
  //   enabled → MFA is on, nothing else to do right now.
  const [step, setStep] = useState(user?.mfa_enabled ? "enabled" : "idle");
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState([]);        // plaintext recovery codes
  const [regenCode, setRegenCode] = useState(""); // TOTP entered on regen path
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    try {
      const r = await api.post("/mfa/setup");
      setData(r.data);
      setStep("setup");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/mfa/verify", { code });
      // BLOCK 36: transition to `codes` step BEFORE any reload so the 10
      // one-time codes from the /setup response can finally be rendered.
      // The old flow called window.location.reload() here, which nuked
      // `data.recovery_codes` from memory and left users with an enrolled
      // account and NO way to reach a recovery-code fallback.
      setCodes(data?.recovery_codes || []);
      setStep("codes");
      toast.success("MFA enabled ,  save these recovery codes now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    // BLOCK 94 Part C ,  subscriber self-service: allow disabling with either
    // a current 6-digit authenticator code OR an unused recovery code (for
    // the lost-authenticator case). Backend (mfa.py::disable) accepts both.
    const c = prompt(
      "Enter your current 6-digit authenticator code ,  or an unused recovery code (XXXX-XXXX) if you've lost your device ,  to disable MFA:"
    );
    if (!c) return;
    const trimmed = c.trim();
    const payload = /^\d{6}$/.test(trimmed)
      ? { code: trimmed }
      : { recovery_code: trimmed };
    try {
      await api.post("/mfa/disable", payload);
      toast.success("MFA disabled");
      const me = await api.get("/auth/me");
      localStorage.setItem("ae_user", JSON.stringify(me.data));
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code ,  MFA not disabled");
    }
  };

  // BLOCK 36: regenerate flow ,  requires a fresh TOTP through /step-up
  // first (backend gate is 10-min step-up TTL). We do both requests
  // sequentially from the same panel so users don't have to touch two UIs.
  const beginRegenerate = () => {
    setStep("regen");
    setRegenCode("");
  };
  const submitRegenerate = async () => {
    if (!/^\d{6}$/.test(regenCode.trim()) && regenCode.trim().length < 8) {
      toast.error("Enter a 6-digit code or a recovery code");
      return;
    }
    setBusy(true);
    try {
      // Refresh the step-up window. Backend accepts either a TOTP code or
      // a recovery code here (see mfa.py::step_up).
      const stepPayload = /^\d{6}$/.test(regenCode.trim())
        ? { code: regenCode.trim() }
        : { recovery_code: regenCode.trim() };
      await api.post("/mfa/step-up", stepPayload);
      const r = await api.post("/mfa/regenerate-recovery-codes");
      setCodes(r.data.recovery_codes || []);
      setStep("codes");
      toast.success("New recovery codes minted ,  old set invalidated");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "mfa_step_up_required") {
        toast.error("Code rejected ,  try the current 6-digit code from your authenticator");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to regenerate");
      }
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard not available ,  use the download button instead");
    }
  };
  const downloadCodes = () => {
    const blob = new Blob(
      [
        `Acquigine ,  MFA recovery codes for ${user?.email || ""}\n`,
        `Generated ${new Date().toISOString()}\n`,
        `Each code works ONCE. Store safely.\n\n`,
        codes.join("\n") + "\n",
      ],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `acquigine-recovery-codes-${(user?.email || "account").split("@")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const finishCodesStep = async () => {
    // Refresh user + reload only AFTER the user acknowledges they saved
    // the codes. This is the point where the setup-time `data` state
    // becomes safe to discard.
    try {
      const me = await api.get("/auth/me");
      localStorage.setItem("ae_user", JSON.stringify(me.data));
    } catch { /* non-fatal */ }
    window.location.reload();
  };

  return (
    <div className="ae-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="h-4 w-4 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Two-Factor Authentication</div>
      </div>

      {step === "enabled" && (
        <div>
          <div className="flex items-center gap-2 text-emerald-400 text-sm" data-testid="mfa-enabled">
            <ShieldCheck className="h-4 w-4" /> MFA is enabled
          </div>
          <p className="text-xs text-slate-500 mt-2">Your account requires a 6-digit code at login. If you lose your authenticator, sign in with one of your recovery codes.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={beginRegenerate}
              data-testid="mfa-regenerate-btn"
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Regenerate recovery codes
            </button>
            <button onClick={disable} data-testid="mfa-disable-btn" className="btn-ghost text-xs">Disable MFA</button>
          </div>
        </div>
      )}

      {step === "idle" && (
        <div>
          <p className="text-sm text-slate-400">Add a TOTP authenticator (Google Authenticator, 1Password, Authy) for extra security.</p>
          <button onClick={start} data-testid="mfa-setup-btn" className="btn-primary text-sm mt-4 inline-flex items-center gap-2">
            <Smartphone className="h-4 w-4" /> Enable MFA
          </button>
        </div>
      )}

      {step === "setup" && data && (
        <div>
          <p className="text-xs text-slate-400 mb-3">Scan this QR with your authenticator app, then enter the 6-digit code below.</p>
          <img src={data.qr_data_url} alt="MFA QR" className="bg-white p-2 rounded-sm w-40 h-40" />
          <p className="text-[10px] text-slate-500 mt-2 font-mono break-all">Secret: {data.secret}</p>
          <div className="mt-4 flex gap-2">
            <input
              data-testid="mfa-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              placeholder="123456"
              className="bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-sm font-mono tabular-nums w-32 tracking-widest"
            />
            <button
              onClick={verify}
              disabled={busy}
              data-testid="mfa-verify-btn"
              className="btn-primary text-sm"
            >
              {busy ? "Verifying…" : "Verify & Enable"}
            </button>
          </div>
        </div>
      )}

      {step === "regen" && (
        <div data-testid="mfa-regen-panel">
          <p className="text-xs text-slate-400 mb-3">
            Enter your current 6-digit authenticator code (or an unused
            recovery code) to generate a fresh set. Your previous
            recovery codes will stop working the moment new ones are issued.
          </p>
          <div className="flex gap-2">
            <input
              data-testid="mfa-regen-code-input"
              value={regenCode}
              onChange={(e) => setRegenCode(e.target.value)}
              placeholder="123456"
              autoFocus
              className="bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-sm font-mono tabular-nums w-40 tracking-widest"
            />
            <button
              onClick={submitRegenerate}
              disabled={busy}
              data-testid="mfa-regen-submit-btn"
              className="btn-primary text-sm"
            >
              {busy ? "Generating…" : "Generate new codes"}
            </button>
            <button
              onClick={() => setStep("enabled")}
              className="btn-ghost text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === "codes" && (
        <div data-testid="mfa-recovery-codes-panel">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-2">
            <KeyRound className="h-3.5 w-3.5" /> Recovery codes ,  shown once
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Save these 10 codes now. Each works exactly once ,  enough for a
            phone-loss emergency. They are only shown here; if you lose them,
            you&apos;ll have to regenerate a new set.
          </p>
          <ul
            data-testid="mfa-recovery-codes-list"
            className="grid grid-cols-2 gap-1.5 mb-4 p-3 rounded-sm bg-[#0A0D12] border border-white/8"
          >
            {codes.map((c, i) => (
              <li
                key={c}
                data-testid={`mfa-recovery-code-${i}`}
                className="font-mono text-sm text-slate-100 tabular-nums select-all"
              >
                {c}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              onClick={copyCodes}
              data-testid="mfa-recovery-copy-btn"
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
            >
              {copied ? (
                <><Check className="h-3.5 w-3.5 text-emerald-400" /> Copied</>
              ) : (
                <><Copy className="h-3.5 w-3.5" /> Copy all</>
              )}
            </button>
            <button
              onClick={downloadCodes}
              data-testid="mfa-recovery-download-btn"
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
            >
              <Download className="h-3.5 w-3.5" /> Download .txt
            </button>
          </div>
          <button
            onClick={finishCodesStep}
            data-testid="mfa-recovery-done-btn"
            className="btn-primary text-sm"
          >
            I&apos;ve saved these ,  continue
          </button>
        </div>
      )}
    </div>
  );
}

function BrandSection({ user }) {
  const [brand, setBrand] = useState(null);
  const [busy, setBusy] = useState(false);
  // White-label branding is now available to all paid tiers (Pro / Business).
  // Super-admin can always edit the platform brand.
  const canEditBrand = user?.role === "super_admin" || isPaidTier(user?.tier);

  useEffect(() => {
    api.get("/admin/brand").then((r) => setBrand(r.data));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/brand", brand);
      toast.success("Brand settings saved");
      // Force layout refresh
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Branding can only be edited by a platform administrator.");
      } else {
        toast.error(e?.response?.data?.detail || "Failed");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!brand) return null;

  return (
    <div className="ae-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <Palette className="h-4 w-4 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">White-Label Branding</div>
        <span className="text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-1.5 rounded-sm">Admin</span>
      </div>

      {!canEditBrand && (
        <div className="ae-card p-4 mb-4 border-amber-500/30 bg-amber-500/5" data-testid="brand-upgrade-banner">
          <div className="text-sm text-amber-300">
            White-label branding can only be edited by a platform administrator. Contact your account owner if you need brand assets updated.
          </div>
        </div>
      )}

      <div className={`grid md:grid-cols-2 gap-3 text-sm ${!canEditBrand ? "opacity-50 pointer-events-none" : ""}`}>
        <Field label="Brand name">
          <input data-testid="brand-name" value={brand.brand_name || ""} onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })} className="inp-dark" />
        </Field>
        <Field label="Tagline">
          <input data-testid="brand-tagline" value={brand.tagline || ""} onChange={(e) => setBrand({ ...brand, tagline: e.target.value })} className="inp-dark" />
        </Field>
        <Field label="Logo URL">
          <input data-testid="brand-logo" value={brand.logo_url || ""} onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })} placeholder="https://…" className="inp-dark" />
        </Field>
        <Field label="Primary color (hex)">
          <div className="flex gap-2 items-center">
            <input data-testid="brand-primary" value={brand.primary_color || ""} onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })} placeholder="#10B981" className="inp-dark font-mono" />
            <div className="h-9 w-9 rounded-sm border border-white/10" style={{ background: brand.primary_color }} />
          </div>
        </Field>
        <Field label="Website">
          <input data-testid="brand-website" value={brand.website || ""} onChange={(e) => setBrand({ ...brand, website: e.target.value })} placeholder="acquigine.com" className="inp-dark" />
        </Field>
        <Field label="Phone">
          <input data-testid="brand-phone" value={brand.phone || ""} onChange={(e) => setBrand({ ...brand, phone: e.target.value })} placeholder="(555) 010-1234" className="inp-dark" />
        </Field>
        <Field label="Email">
          <input data-testid="brand-email" value={brand.email || ""} onChange={(e) => setBrand({ ...brand, email: e.target.value })} placeholder="deals@acquigine.com" className="inp-dark" />
        </Field>
        <Field label="Address">
          <input data-testid="brand-address" value={brand.address || ""} onChange={(e) => setBrand({ ...brand, address: e.target.value })} placeholder="100 Market St, Suite 400, SF CA" className="inp-dark" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Confidentiality notice (appears in document footers)">
            <textarea data-testid="brand-conf" value={brand.confidentiality_notice || ""} onChange={(e) => setBrand({ ...brand, confidentiality_notice: e.target.value })} rows={2} className="inp-dark" />
          </Field>
        </div>
      </div>

      <button onClick={save} disabled={busy || !canEditBrand} data-testid="brand-save-btn" className="btn-primary text-sm mt-4 disabled:opacity-50">
        {busy ? "Saving…" : "Save branding"}
      </button>

      <style>{`.inp-dark { width: 100%; background: #0A0D12; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; padding: 0.5rem 0.625rem; font-size: 0.875rem; outline: none; color: #F8FAFC; }
.inp-dark:focus { border-color: rgba(16,185,129,0.6); }`}</style>
    </div>
  );
}

function SenderProfileSection() {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/sender-profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/me/sender-profile", profile);
      setProfile(r.data);
      toast.success("Sender profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!profile) return null;

  const missing = profile?.completeness?.missing_required || [];
  const ready = profile?.completeness?.ready_for_documents !== false;

  return (
    <div className="ae-card p-6" data-testid="sender-profile-section">
      <div className="flex items-center gap-2 mb-1">
        <UserSquare2 className="h-4 w-4 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Sender Profile</div>
        {ready ? (
          <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5 rounded-sm">Ready</span>
        ) : (
          <span className="ml-2 text-[10px] uppercase tracking-wider bg-rose-500/15 text-rose-200 border border-rose-500/40 px-1.5 py-0.5 rounded-sm" data-testid="sender-profile-incomplete">
            Incomplete: {missing.join(", ")}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Your identity is the primary brand on every document and outreach message you send.
        Acquigine appears only as a small platform credit. Full name, email, and phone are required
        for document generation.
      </p>

      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <Field label="Full name *">
          <input data-testid="sender-full_name" value={profile.full_name || ""} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} className="inp-dark" />
        </Field>
        <Field label="Title">
          <input data-testid="sender-title" value={profile.title || ""} onChange={(e) => setProfile({ ...profile, title: e.target.value })} placeholder="Managing Partner" className="inp-dark" />
        </Field>
        <Field label="Company name">
          <input data-testid="sender-company_name" value={profile.company_name || ""} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} placeholder="e.g. Apex Capital LLC" className="inp-dark" />
        </Field>
        <Field label="Phone *">
          <input data-testid="sender-phone" value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="(555) 010-1234" className="inp-dark" />
        </Field>
        <Field label="Email *">
          <input data-testid="sender-email" value={profile.email || ""} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="inp-dark" />
        </Field>
        <Field label="Mobile (optional)">
          <input data-testid="sender-mobile" value={profile.mobile || ""} onChange={(e) => setProfile({ ...profile, mobile: e.target.value })} className="inp-dark" />
        </Field>
        <Field label="License number (optional)">
          <input data-testid="sender-license_number" value={profile.license_number || ""} onChange={(e) => setProfile({ ...profile, license_number: e.target.value })} placeholder="CA-DRE-01234567" className="inp-dark" />
        </Field>
        <Field label="Company address">
          <input data-testid="sender-company_address" value={profile.company_address || ""} onChange={(e) => setProfile({ ...profile, company_address: e.target.value })} placeholder="100 Market St, Suite 400" className="inp-dark" />
        </Field>
        <Field label="Postal address (CAN-SPAM compliance)">
          <input data-testid="sender-postal_address" value={profile.postal_address || ""} onChange={(e) => setProfile({ ...profile, postal_address: e.target.value })} placeholder="Full mailing address for outreach footer" className="inp-dark" />
        </Field>
        <Field label="Logo URL (cover-page logo)">
          <input data-testid="sender-logo_url" value={profile.logo_url || ""} onChange={(e) => setProfile({ ...profile, logo_url: e.target.value })} placeholder="https://yourdomain.com/logo.png" className="inp-dark" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Default signature block (optional)">
            <textarea data-testid="sender-signature_block" value={profile.signature_block || ""} onChange={(e) => setProfile({ ...profile, signature_block: e.target.value })} rows={3} placeholder={"Jane Buyer\nApex Capital LLC\njane@apex.com  (555) 010-1234"} className="inp-dark" />
          </Field>
        </div>
      </div>

      <button onClick={save} disabled={busy} data-testid="sender-save-btn" className="btn-primary text-sm mt-4 disabled:opacity-50">
        {busy ? "Saving..." : "Save sender profile"}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}
